import { useEffect, useMemo } from "react";
import {
  getSharedStreamConnectionInput,
  processSharedConfigRemoteUpdate,
  useAppStore,
  useServersStore,
  useSharedSyncStore,
} from "../store";
import { openSharedConfigStream, type SharedConfigStreamEvent } from "../sharedConfigClient";
import { useAuthStore } from "../authStore";
import { useSyncStatusStore } from "../syncStatusStore";
import { toast } from "../components/Toasts";

const BASE_RETRY_MS = 1_000;
const MAX_RETRY_MS = 10_000;

export function useSharedConfigSync() {
  const servers = useServersStore((s) => s.servers);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const mode = useAppStore((s) => s.mode);
  const syncStatus = useSyncStatusStore((s) => s.status);
  const sharedServerIds = useMemo(
    () =>
      servers
        .filter((server) => server.sharedConfigId)
        .map((server) => server.id),
    [servers],
  );

  useEffect(() => {
    const canConnect = isAuthenticated
      && mode === "signed-in"
      && (syncStatus === "synced" || syncStatus === "offline");
    if (!canConnect) return;
    if (sharedServerIds.length === 0) return;

    let cancelled = false;
    const activeTokens = new Map<string, symbol>();
    const connections = new Map<string, { close: () => void }>();
    const retryTimers = new Map<string, number>();

    const clearRetry = (serverId: string) => {
      const timer = retryTimers.get(serverId);
      if (timer) {
        window.clearTimeout(timer);
        retryTimers.delete(serverId);
      }
    };

    const closeConnection = (serverId: string) => {
      connections.get(serverId)?.close();
      connections.delete(serverId);
      activeTokens.delete(serverId);
    };

    const scheduleReconnect = (serverId: string, attempt: number) => {
      if (cancelled || document.visibilityState !== "visible") return;
      clearRetry(serverId);
      const delay = Math.min(BASE_RETRY_MS * (2 ** attempt), MAX_RETRY_MS);
      retryTimers.set(serverId, window.setTimeout(() => {
        retryTimers.delete(serverId);
        void connect(serverId, attempt);
      }, delay));
    };

    const connect = async (serverId: string, attempt = 0) => {
      if (cancelled || document.visibilityState !== "visible") return;

      clearRetry(serverId);
      closeConnection(serverId);
      useSharedSyncStore.getState().setServerConnection(serverId, "connecting");

      const connection = await getSharedStreamConnectionInput(serverId);
      if (!connection || cancelled || document.visibilityState !== "visible") {
        useSharedSyncStore.getState().setServerConnection(serverId, "idle");
        return;
      }

      const token = Symbol(serverId);
      activeTokens.set(serverId, token);

      const stream = openSharedConfigStream(
        connection.configId,
        connection.sinceVersion,
        (event: SharedConfigStreamEvent) => {
          if (cancelled || activeTokens.get(serverId) !== token) return;

          if (event.event === "deleted") {
            useSharedSyncStore.getState().setServerConnection(serverId, "offline");
            toast("Shared server was deleted remotely", "error");
            closeConnection(serverId);
            return;
          }

          useSharedSyncStore.getState().setServerConnection(serverId, "live");
          if (event.event === "ready") {
            useSharedSyncStore.getState().setServerRemoteInfo(serverId, {
              updatedAt: event.updatedAt,
              actor: "someone",
            });
            return;
          }

          void processSharedConfigRemoteUpdate(serverId, {
            updatedAt: event.updatedAt,
            updatedByClientId: "remote",
            updatedByDisplayName: null,
          });
        },
      );

      connections.set(serverId, stream);

      stream.ready.then(() => {
        if (cancelled || activeTokens.get(serverId) !== token) return;
        useSharedSyncStore.getState().setServerConnection(serverId, "connecting");
        scheduleReconnect(serverId, attempt + 1);
      }).catch((error: unknown) => {
        if (cancelled || activeTokens.get(serverId) !== token) return;
        const message = error instanceof Error ? error.message : String(error);
        if (message.toLowerCase().includes("abort")) return;

        if (message.includes("401") || message.includes("Session expired") || message.includes("Not authenticated")) {
          useSharedSyncStore.getState().setServerConnection(serverId, "idle");
          closeConnection(serverId);
          return;
        }

        // If shared config no longer exists (404/403), stop retrying and clear the stale reference
        if (message.includes("404") || message.includes("403") || message.includes("Not found") || message.includes("not a member")) {
          useSharedSyncStore.getState().setServerConnection(serverId, "idle");
          closeConnection(serverId);
          return;
        }

        useSharedSyncStore.getState().setServerConnection(serverId, "offline");
        scheduleReconnect(serverId, attempt + 1);
      });
    };

    const openAllVisible = () => {
      if (document.visibilityState !== "visible") return;
      for (const serverId of sharedServerIds) {
        void connect(serverId, 0);
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        openAllVisible();
      } else {
        for (const serverId of sharedServerIds) {
          clearRetry(serverId);
          closeConnection(serverId);
          useSharedSyncStore.getState().setServerConnection(serverId, "idle");
        }
      }
    };

    openAllVisible();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      for (const serverId of sharedServerIds) {
        clearRetry(serverId);
        closeConnection(serverId);
      }
    };
  }, [isAuthenticated, mode, syncStatus, sharedServerIds.join("|")]);
}
